import React from 'react';

const OptionsFundamentals = ({ currentLesson, setCurrentLesson, currentState, updateTutorialState, currentTutorialInfo, userStats, setUserStats, setTutorialComplete, tutorialComplete }) => {
  
  // COMPLETE MODULE CONTENT - ALL 5 MODULES FULLY IMPLEMENTED
  const moduleContent = {
    1: {
      title: "What are Options?",
      icon: "📚",
      content: {
        keyPoints: [
          "Options are contracts that give you rights, not obligations",
          "You pay a premium for the right to buy/sell at a specific price",
          "Options expire on specific dates - use them or lose them",
          "Maximum loss is limited to the premium paid"
        ],
        example: "Think of a movie ticket. You pay $15 for the RIGHT to see a movie at 8 PM. If you don't show up, you lose the $15, but you don't owe anything more."
      },
      quiz: [
        {
          id: 'q1',
          question: "What is an option?",
          options: [
            "A contract that gives you the right to buy/sell an asset",
            "A guaranteed investment return",
            "A type of savings account", 
            "A loan from the bank"
          ],
          correct: 0,
          explanation: "Correct! An option is a contract that gives you the RIGHT (not obligation) to buy or sell an underlying asset at a specific price within a certain time period."
        },
        {
          id: 'q2',
          question: "When you buy an option, what do you pay?",
          options: ["The strike price", "The premium", "The market price", "Nothing upfront"],
          correct: 1,
          explanation: "Correct! You pay the PREMIUM when buying an option. This is the cost of the contract and your maximum loss."
        }
      ]
    },
    2: {
      title: "Calls vs Puts",
      icon: "📈",
      content: {
        keyPoints: [
          "CALL options give you the right to BUY at a specific price",
          "PUT options give you the right to SELL at a specific price", 
          "Buy calls when you expect prices to rise",
          "Buy puts when you expect prices to fall"
        ],
        example: "BTC at $50,000. Buy a $55,000 call if you think BTC will go above $55,000. Buy a $45,000 put if you think BTC will drop below $45,000."
      },
      quiz: [
        {
          id: 'q1',
          question: "When would you buy a CALL option?",
          options: ["When you expect prices to fall", "When you expect prices to rise", "When you want to sell", "When you're unsure"],
          correct: 1,
          explanation: "Correct! You buy CALL options when you expect the underlying asset's price to RISE above the strike price."
        },
        {
          id: 'q2', 
          question: "What does a PUT option give you the right to do?",
          options: ["Buy at strike price", "Sell at strike price", "Hold forever", "Get premium back"],
          correct: 1,
          explanation: "Correct! PUT options give you the right to SELL the underlying asset at the strike price."
        }
      ]
    },
    3: {
      title: "Strike Prices & Expiration",
      icon: "🎯",
      content: {
        keyPoints: [
          "Strike price is the agreed-upon price for buying/selling",
          "In-the-money (ITM): profitable to exercise",
          "Out-of-the-money (OTM): not profitable to exercise",
          "At-the-money (ATM): strike price equals current price"
        ],
        example: "BTC at $50,000. A $48,000 call is ITM (+$2,000 value). A $52,000 call is OTM (no intrinsic value). A $50,000 call is ATM."
      },
      quiz: [
        {
          id: 'q1',
          question: "If BTC is at $50,000, which call option is in-the-money?",
          options: ["$52,000 call", "$48,000 call", "$55,000 call", "$60,000 call"],
          correct: 1,
          explanation: "Correct! The $48,000 call is ITM because you can buy BTC for $48,000 when it's worth $50,000 - a $2,000 profit."
        },
        {
          id: 'q2',
          question: "What happens when options expire?",
          options: ["They automatically exercise", "They become worthless if OTM", "You get premium back", "They extend 30 days"],
          correct: 1,
          explanation: "Correct! If options expire out-of-the-money, they become worthless. If ITM, they may auto-exercise."
        }
      ]
    },
    4: {
      title: "Option Pricing Basics",
      icon: "💰",
      content: {
        keyPoints: [
          "Premium = Intrinsic Value + Time Value",
          "Intrinsic value: profit if exercised now",
          "Time value: extra premium for time remaining",
          "Volatility increases option prices"
        ],
        example: "BTC at $50,000. $48,000 call trading at $3,000. Intrinsic value = $2,000 ($50k - $48k). Time value = $1,000 extra."
      },
      quiz: [
        {
          id: 'q1',
          question: "What two components make up an option's premium?",
          options: ["Buy price + sell price", "Intrinsic value + time value", "Strike + expiration", "Bid + ask"],
          correct: 1,
          explanation: "Correct! Premium = Intrinsic Value (profit if exercised now) + Time Value (extra premium for time remaining)."
        },
        {
          id: 'q2',
          question: "What happens to time value as expiration approaches?",
          options: ["It increases", "It decreases", "It stays the same", "It doubles"],
          correct: 1,
          explanation: "Correct! Time value decreases as expiration approaches - this is called 'time decay' or theta."
        }
      ]
    },
    5: {
      title: "Your First Options Trade",
      icon: "🚀",
      content: {
        keyPoints: [
          "Start with simple buy calls or puts",
          "Use only money you can afford to lose",
          "Choose liquid options with tight bid-ask spreads",
          "Have a clear profit target and exit strategy"
        ],
        example: "Practice scenario: BTC at $50,000. Buy 1 $52,000 call expiring in 30 days for $1,500. Profit if BTC goes above $53,500 ($52,000 + $1,500 premium)."
      },
      quiz: [
        {
          id: 'q1',
          question: "For a beginner, what's the best first options strategy?",
          options: ["Complex spreads", "Simple buying calls/puts", "Naked selling", "Multiple leg strategies"],
          correct: 1,
          explanation: "Correct! Beginners should start with simple buying of calls or puts to learn the basics with limited risk."
        },
        {
          id: 'q2',
          question: "What's the maximum loss when buying an option?",
          options: ["Unlimited", "The premium paid", "The strike price", "50% of premium"],
          correct: 1,
          explanation: "Correct! When buying options, your maximum loss is limited to the premium you paid - nothing more!"
        }
      ]
    }
  };

  const currentModuleContent = moduleContent[currentLesson];
  if (!currentModuleContent) return null;

  if (currentState.phase === 'intro') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-4">{currentModuleContent.icon}</div>
          <h3 className="text-white text-2xl font-bold mb-2">Module {currentLesson}: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Essential options trading concepts</p>
        </div>

        <div className="bg-atticus-navy-800 rounded-lg p-6 space-y-4">
          <div className="border-l-4 border-blue-500 pl-4">
            <h4 className="text-white font-bold mb-2">💡 Key Concepts</h4>
            <div className="space-y-2">
              {currentModuleContent.content.keyPoints.map((point, index) => (
                <p key={index} className="text-gray-300 text-sm">• {point}</p>
              ))}
            </div>
          </div>

          <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4">
            <h5 className="text-yellow-400 font-bold mb-2">🏪 Practical Example</h5>
            <p className="text-white text-sm">{currentModuleContent.content.example}</p>
          </div>
        </div>

        <button
          onClick={() => updateTutorialState({ phase: 'quiz' })}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold transition-colors"
        >
          Take Knowledge Check →
        </button>
      </div>
    );
  }

  if (currentState.phase === 'quiz') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-white text-2xl font-bold mb-2">Knowledge Check: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Master these concepts to continue</p>
        </div>

        {currentModuleContent.quiz.map((question, qIndex) => {
          const userAnswer = currentState.quizAnswers[question.id];
          const isAnswered = userAnswer !== undefined;
          const isCorrect = userAnswer === question.correct;

          return (
            <div key={question.id} className="bg-atticus-navy-800 rounded-lg p-6">
              <div className="mb-4">
                <h4 className="text-white font-bold mb-3">Question {qIndex + 1}: {question.question}</h4>
                <div className="space-y-2">
                  {question.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => updateTutorialState({ 
                        quizAnswers: { ...currentState.quizAnswers, [question.id]: oIndex }
                      })}
                      disabled={isAnswered}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        isAnswered
                          ? oIndex === question.correct 
                            ? 'bg-green-500/20 border-green-500 text-green-300'
                            : oIndex === userAnswer
                              ? 'bg-red-500/20 border-red-500 text-red-300'
                              : 'bg-gray-700 border-gray-600 text-gray-400'
                          : 'bg-atticus-navy-700 border-gray-600 text-gray-300 hover:border-blue-500 hover:text-white'
                      }`}
                    >
                      {option}
                      {isAnswered && oIndex === question.correct && <span className="float-right text-green-400">✓</span>}
                      {isAnswered && oIndex === userAnswer && oIndex !== question.correct && <span className="float-right text-red-400">✗</span>}
                    </button>
                  ))}
                </div>
              </div>

              {isAnswered && (
                <div className={`p-3 rounded-lg border ${isCorrect ? 'bg-green-500/20 border-green-500' : 'bg-red-500/20 border-red-500'}`}>
                  <p className={`text-sm ${isCorrect ? 'text-green-300' : 'text-red-300'}`}>
                    {isCorrect ? '✅ Correct! ' : '❌ Incorrect. '}{question.explanation}
                  </p>
                </div>
              )}
            </div>
          );
        })}

        {Object.keys(currentState.quizAnswers).length === currentModuleContent.quiz.length && (
          <div className="text-center">
            {currentModuleContent.quiz.every(q => currentState.quizAnswers[q.id] === q.correct) ? (
              <div className="space-y-4">
                <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
                  <div className="text-green-400 font-bold text-lg">🎉 Module {currentLesson} Complete!</div>
                  <div className="text-green-300">You've mastered {currentModuleContent.title}!</div>
                </div>
                <button
                  onClick={() => {
                    const moduleXP = currentTutorialInfo.xp_per_module;
                    setUserStats(prev => ({
                      ...prev,
                      completed_lessons: [...prev.completed_lessons, `options_fundamentals_module_${currentLesson}`],
                      total_xp: prev.total_xp + moduleXP,
                      badges: currentLesson === 1 ? [...prev.badges, 'Options Basics'] : prev.badges
                    }));

                    if (currentLesson < currentTutorialInfo.modules) {
                      setCurrentLesson(currentLesson + 1);
                      updateTutorialState({ phase: 'intro', quizAnswers: {} });
                    } else {
                      setTutorialComplete(true);
                      setUserStats(prev => ({
                        ...prev,
                        completed_tutorials: [...prev.completed_tutorials, 'options_fundamentals'],
                        certification_progress: Math.min(prev.certification_progress + 20, 100),
                        badges: [...prev.badges, 'Options Fundamentals Master']
                      }));
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-bold transition-colors"
                >
                  {currentLesson < currentTutorialInfo.modules ? `Continue to Module ${currentLesson + 1} →` : 'Complete Tutorial →'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-orange-500/20 border border-orange-500 rounded-lg p-4">
                  <div className="text-orange-400 font-bold">📖 Review Required</div>
                  <div className="text-orange-300">Please review and try again.</div>
                </div>
                <button
                  onClick={() => updateTutorialState({ quizAnswers: {}, phase: 'intro' })}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
                >
                  Review Module {currentLesson}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default OptionsFundamentals;
